import { ZfFileItem, ZfFilesUpload, ZfFormField, ZfIcon, ZfLink, ZfStack, ZfTypography } from '@zafire/react';
import type { CSSProperties, FunctionComponent } from 'react';
import { useMemo } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { DocumentDto, DocumentFileDto } from '../../../api/generated/client';
import { AVAILABLE_LANGUAGES } from '../../../i18n/i18nextInit';
import { sortDocumentFileByLanguage } from '../../../utils/fileUtils';
import { AdminDocumentFileItem } from './AdminDocumentFileItem';

interface IProps {
    isCreateMode: boolean;
    control: Control<DocumentDto>;
    setValue: UseFormSetValue<DocumentDto>;
}

export const AdminDocumentUpload: FunctionComponent<IProps> = ({ isCreateMode, control, setValue }) => {
    const { t } = useTranslation();

    const [name, documentFiles] = useWatch({ control, name: ['name', 'documentFiles'] });

    const onDocumentFileItemChange = (documentFile: DocumentFileDto, title: string) => {
        // Find the old document file to check if the language changed
        const oldFile = documentFiles?.find((file) => (file.id ? file.id === documentFile.id : file.fileName === documentFile.fileName));
        const oldLanguage = oldFile?.language;

        // Update name: remove old language entry if language changed, then add/update the new language entry
        const updatedName = { ...name };
        if (oldLanguage && oldLanguage !== documentFile.language) delete updatedName[oldLanguage];

        if (documentFile.language) updatedName[documentFile.language] = title;
        setValue('name', updatedName);

        // Update documentFiles: replace the file in the array
        setValue('documentFiles', [
            ...(documentFiles?.length ? documentFiles.filter((file) => (file.id ? file.id !== documentFile.id : file.fileName !== documentFile.fileName)) : []),
            documentFile,
        ]);
    };

    const updateFile = (documentFile: DocumentFileDto) => {
        setValue('documentFiles', [...(documentFiles?.length ? documentFiles.filter((file) => file.id !== documentFile.id) : []), documentFile]);
    };

    const isDuplicate = (name: string) => documentFiles && documentFiles.filter((d) => d.fileName === name).length > 0;

    const addFiles = (droppedFiles: File[]) => {
        const [n, df] = droppedFiles.reduce(
            (acc, droppedFile, index) => {
                if (isDuplicate(droppedFile.name)) return acc;
                const language = AVAILABLE_LANGUAGES[index];

                acc[0] = { ...acc[0], [language]: droppedFile.name };
                acc[1] = [
                    ...acc[1],
                    {
                        fileName: droppedFile.name,
                        file: droppedFile,
                        language: undefined,
                        mediaType: droppedFile.type,
                    },
                ];

                return acc;
            },
            [name, documentFiles ?? ([] as DocumentFileDto[])],
        );

        setValue('name', n);
        setValue('documentFiles', df);
    };

    const removeFile = (documentFile: DocumentFileDto) => {
        const updatedFiles = (documentFiles != null ? documentFiles : []).filter((docFile) =>
            documentFile.fileName ? docFile.fileName !== documentFile.fileName : docFile.file !== documentFile.file,
        );
        setValue('documentFiles', updatedFiles);

        if (documentFile.language && name) {
            const updatedName = { ...name };
            delete updatedName[documentFile.language];
            setValue('name', updatedName);
        }
    };

    const fileUploadCount = useMemo<number>(
        () => (documentFiles?.length ? AVAILABLE_LANGUAGES.length - documentFiles.length : AVAILABLE_LANGUAGES.length),
        [documentFiles?.length],
    );

    return (
        <ZfStack direction="column" spacing="medium" justify="center">
            {isCreateMode ? (
                <ZfFormField mandatory={true} helperText={t('dndRequiredLabel', { count: fileUploadCount })}>
                    <ZfFilesUpload
                        id="input-multiple-file-upload"
                        style={{ '--dropzone-height': '150px' } as CSSProperties}
                        maxFilesCount={4}
                        text={t('')}
                        hasDropzone={true}
                        onFileInputChange={(e) => addFiles(e.detail)}
                    >
                        <ZfStack direction="column" spacing="extra-small" slot="dropzone" alignment="center" justify="center">
                            <ZfIcon style={{ color: 'var(--fg-color-action-normal )' }} name="upload" size="extra-extra-large" />
                            <ZfTypography variant="label-small" color="action">
                                <Trans i18nKey="dndFileCountLabel" components={{ b: <b /> }} count={AVAILABLE_LANGUAGES.length} />{' '}
                                <ZfLink size="small" alias="a">
                                    {t('dndSelectLabel')}
                                </ZfLink>
                            </ZfTypography>
                        </ZfStack>
                        {documentFiles?.map((file) => (
                            <ZfFileItem key={file.id} name={file.fileName} progress="uploaded" downloadable={false} onFileDelete={() => removeFile(file)} />
                        ))}
                    </ZfFilesUpload>
                </ZfFormField>
            ) : null}
            <ZfStack direction="column" spacing="large">
                {documentFiles?.sort(sortDocumentFileByLanguage).map((docFile) => (
                    <AdminDocumentFileItem
                        key={`${docFile.language}-${docFile.fileName}`}
                        documentFile={docFile}
                        onChange={onDocumentFileItemChange}
                        name={docFile.language ? name[docFile.language] : ''}
                        updateFile={!isCreateMode ? updateFile : undefined}
                        control={control}
                    />
                ))}
            </ZfStack>
        </ZfStack>
    );
};
